import { Outlet } from "react-router-dom";
import { useLocation } from "react-router-dom";

import { Suspense, useState, useEffect, useRef, useCallback } from "react";
import RouteFallback from "../components/RouteFallback";
import { useAuth } from "../../contexts/AuthContext";
import { useKYC } from "../../contexts/KYCContext";
import CasinoSidebar from "./CasinoSidebar";
import CasinoHeader from "./CasinoHeader";
import MobileBottomNavigation from "./MobileBottomNavigation";

import BuyPageMobileNavigation from "./BuyPageMobileNavigation";
import Footer from "./Footer";
import GamesPageMobileNavigation from "./GamesPageMobileNavigation";
import NewUserSplash from "../components/ui/NewUserSplash";

// Preload critical images for faster homepage loading
const PreloadCriticalImages = () => {
  useEffect(() => {
    const criticalImages = [
      "/assets/3d/cluck_stack_05.png", // Welcome bonus
      "/assets/3d/cluck_stack_01.png", // Daily reward
      "/assets/3d/cup-render.png", // VIP program
      "/assets/bg/bg-1-1600.jpg", // Card background
    ];

    criticalImages.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  return null;
};

/**
 * Persistent Casino Layout - Renders sidebar and header once
 * Only the main content (Outlet) changes when navigating between casino pages
 * This prevents sidebar/header flickering during navigation
 */
const PersistentCasinoLayout = () => {
  const { isLoggedIn, loading: authLoading } = useAuth();
  const location = useLocation();
  const { isVerified, loading: kycLoading } = useKYC();

  // Single source of truth for sidebar collapse state. Previously this
  // lived in both CasinoSidebar and here, with a window.dispatchEvent
  // bridge to sync them; that arrangement raced on remount (child effects
  // fire before parent effects, so the sidebar's initial-collapse event
  // was dispatched before the parent had attached its listener and the
  // parent's margin stayed at full-width). Owning state here and passing
  // props down eliminates the race entirely.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const handleSidebarToggle = useCallback(() => {
    setSidebarCollapsed((v) => !v);
  }, []);

  // Anonymous visitors get a collapsed sidebar by default (less weight on
  // the page they're trying to evaluate); logged-in users get it expanded.
  // Runs once after auth first resolves, and the ref-flag guards against a
  // re-fire later overriding a manual toggle or a fresh sign-in.
  const initialCollapseAppliedRef = useRef(false);
  useEffect(() => {
    if (authLoading || initialCollapseAppliedRef.current) return;
    initialCollapseAppliedRef.current = true;
    if (!isLoggedIn) setSidebarCollapsed(true);
  }, [authLoading, isLoggedIn]);

  const isLobbyRoute = location.pathname === "/";
  const shouldShowOnboarding =
    isLobbyRoute && isLoggedIn && !isVerified && !kycLoading;

  // Margin width tracks the sidebar exactly: w-16 (64px) collapsed,
  // w-64 (256px) expanded. Both classes are derived from the same state
  // value so they cannot drift apart.
  const mainContentMargin = sidebarCollapsed ? "md:ml-16" : "md:ml-64";

  return (
    <div className="min-h-screen bg-background text-white">
      {/* Preload critical images for performance */}
      <PreloadCriticalImages />
      {/* Logged-in onboarding overlay - lobby only for unverified users */}
      <NewUserSplash enabled={shouldShowOnboarding} />

      {/* Persistent Sidebar - Always show */}
      <CasinoSidebar
        collapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      />

      {/* Main Content Area - Responsive to sidebar state */}
      <div className={`flex flex-col min-h-screen ${mainContentMargin}`}>
        {/* Persistent Header - Never re-renders */}
        <CasinoHeader />

        {/* Dynamic Content - Only this changes on navigation. Lazy route
            chunks suspend here so the sidebar/header stay visible; with
            React Router v7 transitions this fallback only appears on hard
            loads of a lazy route, not in-app navigation. */}
        <main className="flex-1 overflow-auto border-l border-t border-border rounded-tl-lg pb-6 md:pb-0">
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </main>

        {/* Footer */}
        <Footer />
      </div>

      {/* Buy Page Mobile Navigation - Shows above main mobile nav on Buy page only */}
      <BuyPageMobileNavigation />

      {/* Games Page Mobile Navigation - Shows on games category pages */}
      <GamesPageMobileNavigation />

      {/* Mobile Bottom Navigation */}
      <MobileBottomNavigation />
    </div>
  );
};

export default PersistentCasinoLayout;
